import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  UserIcon, 
  FolderIcon, 
  TagIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArchiveBoxIcon,
  MapPinIcon,
  FlagIcon,
  CalendarIcon,
  BellIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { 
  EnhancedDocumentFormData, 
  FormSection,
  Jurisdiction,
  DocumentPurpose,
  DocumentLifecycleStage,
  RetentionPolicy
} from '../types/DocumentTypes';
import { AITagSuggestion } from './AITagSuggestion';
import { AIRelatedDocuments } from './AIRelatedDocuments';
import { AIConfidencePreview } from './AIConfidencePreview';
import { MCPProcessingProfileSelector } from './MCPProcessingProfileSelector';
import { useContacts } from '../../contacts/hooks/useContacts';
import { matterApi, Matter } from '../../../config/api';
import {
  listDocumentTypeOptionsForPracticeAreas,
  listMatterPhaseOptions,
} from '../taxonomy/legalDocumentTaxonomy';

interface EnhancedDocumentMetadataFormProps {
  formData: EnhancedDocumentFormData;
  onInputChange: (field: keyof EnhancedDocumentFormData, value: any) => void;
  mode: 'build' | 'upload';
  realmId: string;
  uploadedFiles?: File[];
  onAutofillTitleAndDescription?: () => void;
  isAutofillingTitleAndDescription?: boolean;
  canAutofillTitleAndDescription?: boolean;
  initialClientName?: string;
  initialMatterName?: string;
  initialMatterNumber?: string;
}

export const EnhancedDocumentMetadataForm: React.FC<EnhancedDocumentMetadataFormProps> = ({
  formData,
  onInputChange,
  mode,
  // realmId,
  uploadedFiles = [],
  onAutofillTitleAndDescription,
  isAutofillingTitleAndDescription = false,
  canAutofillTitleAndDescription = false,
  initialClientName = '',
  initialMatterName = '',
  initialMatterNumber = '',
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['essential-info', 'document-details']));
  const [selectedTags, setSelectedTags] = useState<string[]>(formData.tags);
  const [newTag, setNewTag] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  
  // Diagnostic: Track setClientSearch calls
  const setClientSearchWithLogging = (value: string) => {
    setClientSearch(value);
  };
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchError, setClientSearchError] = useState<string | null>(null);
  const [clientSelected, setClientSelected] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  
  // Calculate dropdown position
  const calculateDropdownPosition = useCallback(() => {
    if (clientInputRef.current) {
      const rect = clientInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px below the input
        left: rect.left,
        width: Math.max(rect.width, 384) // At least 384px wide
      });
    }
  }, []);

  // Update dropdown position when showing dropdown
  useEffect(() => {
    if (clientSearch.length > 0 && !clientSelected) {
      calculateDropdownPosition();
      // Recalculate on resize
      const handleResize = () => calculateDropdownPosition();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [clientSearch, clientSelected, calculateDropdownPosition]);
  
  // Use real contacts API
  const { contacts, fetchContacts } = useContacts();
  
  // Matter state management
  const [matters, setMatters] = useState<Matter[]>([]);
  const [mattersLoading, setMattersLoading] = useState(false);
  const [mattersError, setMattersError] = useState<string | null>(null);
  
  // Debounced client search function
  const debouncedClientSearch = useCallback((searchTerm: string) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Don't search for empty strings
    if (!searchTerm.trim()) {
      setClientSearchLoading(false);
      setClientSearchError(null);
      return;
    }
    
    // Set loading state immediately
    setClientSearchLoading(true);
    setClientSearchError(null);
    
    // Debounce the API call
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        await fetchContacts({ search: searchTerm });
        setClientSearchError(null);
      } catch (error) {
        console.error('Client search error:', error);
        setClientSearchError(error instanceof Error ? error.message : 'Search failed');
      } finally {
        setClientSearchLoading(false);
      }
    }, 300); // 300ms debounce delay
  }, [fetchContacts]);

  // Fetch matters for selected client
  const fetchMattersForClient = useCallback(async (clientId: string) => {
    if (!clientId) {
      setMatters([]);
      return;
    }
    
    setMattersLoading(true);
    setMattersError(null);
    
    try {
      const response = await matterApi.getByClient(clientId);
      
      // Handle DMS client matters response format: { client_id, matters, total }
      const matters = (response as any).matters || response.data || [];
      
      setMatters(matters);
    } catch (error) {
      console.error('Matter fetch error:', error);
      setMattersError(error instanceof Error ? error.message : 'Failed to fetch matters');
      setMatters([]);
    } finally {
      setMattersLoading(false);
    }
  }, []);
  
  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);



  // Mock data - replace with actual API calls
  const mockJurisdictions: Jurisdiction[] = [
    { id: '1', name: 'Federal Court', type: 'federal', code: 'FED' },
    { id: '2', name: 'California Superior Court', type: 'state', state: 'CA', code: 'CA-SUP' },
    { id: '3', name: 'New York Supreme Court', type: 'state', state: 'NY', code: 'NY-SUP' },
    { id: '4', name: 'Los Angeles County Court', type: 'county', state: 'CA', court: 'LA', code: 'CA-LA' },
  ];

  const mockDocumentPurposes: DocumentPurpose[] = [
    { id: '1', name: 'Litigation Support', category: 'litigation', description: 'Documents supporting litigation cases' },
    { id: '2', name: 'Contract Negotiation', category: 'transactional', description: 'Contract-related documents' },
    { id: '3', name: 'Discovery', category: 'litigation', description: 'Discovery and evidence documents' },
    { id: '4', name: 'Compliance', category: 'compliance', description: 'Regulatory compliance documents' },
    { id: '5', name: 'General Legal', category: 'general', description: 'General legal documents' },
  ];

  const mockLifecycleStages: DocumentLifecycleStage[] = [
    { 
      stage: 'draft', 
      label: 'Draft', 
      description: 'Initial version under development',
      color: 'text-gray-600 bg-gray-50 border-gray-200',
      icon: '📝',
      permissions: { canEdit: true, canDelete: true, canShare: false, requiresApproval: false }
    },
    { 
      stage: 'review', 
      label: 'Review', 
      description: 'Under review by legal team',
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      icon: '👀',
      permissions: { canEdit: false, canDelete: false, canShare: true, requiresApproval: true }
    },
    { 
      stage: 'final', 
      label: 'Final', 
      description: 'Approved and finalized version',
      color: 'text-green-600 bg-green-50 border-green-200',
      icon: '✅',
      permissions: { canEdit: false, canDelete: false, canShare: true, requiresApproval: false }
    },
    { 
      stage: 'archived', 
      label: 'Archived', 
      description: 'Archived for retention',
      color: 'text-lex-accent bg-lex-accent-soft border-lex-accent',
      icon: '📦',
      permissions: { canEdit: false, canDelete: false, canShare: false, requiresApproval: false }
    },
  ];

  const mockRetentionPolicies: RetentionPolicy[] = [
    { id: '1', name: '7 Years (Standard Legal)', description: 'Standard legal retention period', retentionPeriod: 2555, complianceRules: ['Legal standard retention'] },
    { id: '2', name: '10 Years (Extended)', description: 'Extended legal retention period', retentionPeriod: 3650, complianceRules: ['Extended legal retention'] },
    { id: '3', name: 'Permanent (Archive)', description: 'Permanent document retention', retentionPeriod: 36500, complianceRules: ['Permanent retention'] },
    { id: '4', name: 'Until Case Closure', description: 'Retain until case is closed', retentionPeriod: 0, complianceRules: ['Case-based retention'] },
  ];

  // Mock clients removed - using actual contacts from API instead

  // Removed mockMatters - using real API data now



  // Form sections configuration - Optimized for desktop document upload workflow
  const formSections: FormSection[] = [
    {
      id: 'essential-info',
      title: 'Essential Information',
      description: 'Required fields for document upload',
      icon: '📋',
      fields: ['title', 'clientId', 'matterId'],
      isExpanded: true,
      isRequired: true
    },
    {
      id: 'document-details',
      title: 'Document Details',
      description: 'Description, tags, and legal classification',
      icon: '📄',
      fields: ['description', 'tags', 'jurisdiction', 'documentPurpose', 'lifecycleStage'],
      isExpanded: true,
      isRequired: false
    },
    {
      id: 'processing-options',
      title: 'Processing & AI',
      description: 'AI processing settings and automation options',
      icon: '🤖',
      fields: ['aiProcessing', 'aiProcessingProfile', 'aiOnlyVisibility'],
      isExpanded: false,
      isRequired: false
    },
    {
      id: 'security-compliance',
      title: 'Security & Compliance',
      description: 'Confidentiality, retention, and responsible attorney',
      icon: '🔒',
      fields: ['confidentiality', 'retentionPolicy', 'responsibleAttorney'],
      isExpanded: false,
      isRequired: false
    },
    {
      id: 'dates-reminders',
      title: 'Dates & Reminders',
      description: 'Effective dates, expiry dates, and reminder settings',
      icon: '📅',
      fields: ['effectiveDate', 'expiryDate', 'reminderEnabled', 'reminderDays'],
      isExpanded: false,
      isRequired: false
    },
    {
      id: 'relations-versioning',
      title: 'Relations & Versioning',
      description: 'Related documents and version management',
      icon: '🔗',
      fields: ['relatedDocuments', 'version', 'customVersionLabel', 'versionNotes'],
      isExpanded: false,
      isRequired: false
    }
  ].filter((section) => {
    if (mode !== 'upload') {
      return true;
    }

    return section.id === 'essential-info' || section.id === 'document-details';
  });

  // Update tags when formData changes
  useEffect(() => {
    setSelectedTags(formData.tags);
  }, [formData.tags]);

  // Update client search when clientId changes
  useEffect(() => {
    if (formData.clientId && contacts.length > 0) {
      const selectedClient = contacts.find(c => c.id === formData.clientId);
      if (selectedClient) {
        setClientSearchWithLogging(selectedClient.name);
        setClientSelected(true);
      } else if (initialClientName) {
        setClientSearchWithLogging(initialClientName);
        setClientSelected(true);
      }
    } else if (formData.clientId && initialClientName) {
      setClientSearchWithLogging(initialClientName);
      setClientSelected(true);
    } else if (!formData.clientId) {
      setClientSelected(false);
    }
    // DON'T clear the search term automatically - only when user explicitly clears it
  }, [formData.clientId, contacts, initialClientName]);

  // Fetch matters when clientId changes
  useEffect(() => {
    if (formData.clientId) {
      fetchMattersForClient(formData.clientId);
    } else {
      setMatters([]);
    }
  }, [formData.clientId, fetchMattersForClient]);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleTagAdd = (tag: string) => {
    if (tag && !selectedTags.includes(tag)) {
      const updatedTags = [...selectedTags, tag];
      setSelectedTags(updatedTags);
      onInputChange('tags', updatedTags);
    }
    setNewTag('');
  };

  const handleTagRemove = (tagToRemove: string) => {
    const updatedTags = selectedTags.filter(tag => tag !== tagToRemove);
    setSelectedTags(updatedTags);
    onInputChange('tags', updatedTags);
  };

  const getConfidentialityColor = (level: string) => {
    switch (level) {
      case 'public': return 'text-green-600 bg-green-50 border-green-200';
      case 'internal': return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
      case 'confidential': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'restricted': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-lex-accent bg-lex-accent-soft border-lex-accent';
    }
  };

  const getLifecycleStageColor = (stage: string) => {
    const stageConfig = mockLifecycleStages.find(s => s.stage === stage);
    return stageConfig?.color || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const calculateAutoArchiveDate = (retentionPolicy: string) => {
    const policy = mockRetentionPolicies.find(p => p.id === retentionPolicy);
    if (policy && policy.retentionPeriod > 0) {
      const archiveDate = new Date();
      archiveDate.setDate(archiveDate.getDate() + policy.retentionPeriod);
      return archiveDate.toISOString().split('T')[0];
    }
    return null;
  };

  const initialMatterLabel = [initialMatterNumber, initialMatterName].filter(Boolean).join(': ');
  const selectedMatterInOptions = !!formData.matterId && matters.some((matter) => matter.id === formData.matterId);
  const selectedMatter = useMemo(
    () => matters.find((matter) => matter.id === formData.matterId),
    [formData.matterId, matters],
  );
  const documentTypeOptions = useMemo(
    () => listDocumentTypeOptionsForPracticeAreas([selectedMatter?.practice_area || 'other']),
    [selectedMatter?.practice_area],
  );
  const matterPhaseOptions = useMemo(() => listMatterPhaseOptions(), []);

  return (
    <div className="space-y-4">
      {/* Form Sections */}
      {formSections.map((section) => (
        <div key={section.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Section Header */}
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{section.icon}</span>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {section.description}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {section.isRequired && (
                <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                  Required
                </span>
              )}
              {expandedSections.has(section.id) ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {/* Section Content */}
          {expandedSections.has(section.id) && (
            <div className="px-4 pb-4 space-y-3">
              {/* Essential Information Section */}
              {section.id === 'essential-info' && (
                <>
                  {/* Title */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                        Document Title
                      </label>
                      {canAutofillTitleAndDescription && onAutofillTitleAndDescription && (
                        <button
                          type="button"
                          onClick={onAutofillTitleAndDescription}
                          disabled={isAutofillingTitleAndDescription}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-lex-accent/30 bg-lex-accent-soft px-2.5 py-1 text-xs font-medium text-lex-accent transition-colors hover:border-lex-accent hover:bg-lex-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
                          title="Use AI to suggest the title and description from the uploaded document"
                        >
                          {isAutofillingTitleAndDescription ? (
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border border-current border-t-transparent" />
                          ) : (
                            <SparklesIcon className="h-3.5 w-3.5" />
                          )}
                          {isAutofillingTitleAndDescription ? 'Reading document...' : 'AI Fill'}
                        </button>
                      )}
                    </div>
                    <input
                      id="document-title-input"
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        console.log('📝 [EnhancedDocumentMetadataForm] Title input change:', {
                          value: e.target.value,
                          length: e.target.value.length,
                          type: typeof e.target.value
                        });
                        onInputChange('title', e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent text-base font-medium placeholder-gray-400"
                      placeholder="Enter document title..."
                    />
                  </div>

                  {/* Client and Matter */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                        <UserIcon className="h-4 w-4" />
                        Client
                      </label>
                      <div className="relative z-50 overflow-visible">
                        <input
                          ref={clientInputRef}
                          type="text"
                          value={clientSearch}
                          onChange={(e) => {
                            const searchValue = e.target.value;
                            setClientSearchWithLogging(searchValue);
                            setClientSelected(false); // User is typing, not selected
                            debouncedClientSearch(searchValue);
                          }}
                          placeholder="Search clients..."
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        />
                        {clientSearch && (
                          <button
                            type="button"
                            onClick={() => {
                              setClientSearchWithLogging('');
                              onInputChange('clientId', '');
                              setClientSelected(false);
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                        <FolderIcon className="h-4 w-4" />
                        Matter
                        {mattersLoading && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent"></div>
                        )}
                      </label>
                      <select
                        value={formData.matterId}
                        onChange={(e) => onInputChange('matterId', e.target.value)}
                        disabled={mattersLoading}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {!formData.clientId 
                            ? "Select client first..." 
                            : mattersLoading 
                            ? "Loading matters..." 
                            : mattersError 
                            ? "Error loading matters" 
                            : matters.length === 0 
                            ? "No matters available" 
                            : "Select matter..."}
                        </option>
                        {matters.map((matter) => (
                          <option key={matter.id} value={matter.id}>
                            {matter.title} ({matter.status})
                          </option>
                        ))}
                        {formData.matterId && !selectedMatterInOptions && initialMatterLabel && (
                          <option value={formData.matterId}>
                            {initialMatterLabel}
                          </option>
                        )}
                      </select>
                      {mattersError && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {mattersError}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Document Details Section */}
              {section.id === 'document-details' && (
                <>
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => onInputChange('description', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                      placeholder="Describe the document..."
                      rows={3}
                    />
                  </div>

                  {/* AI Tag Suggestions */}
                  <AITagSuggestion
                    documentTitle={formData.title}
                    documentContent={formData.description}
                    selectedTags={selectedTags}
                    onTagsSuggested={(tags) => onInputChange('tags', tags)}
                    onTagAdd={handleTagAdd}
                  />

                  {/* Manual Tags */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <TagIcon className="h-4 w-4" />
                      Tags
                    </label>
                    
                    {/* Selected Tags */}
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedTags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-lex-accent-soft text-lex-accent text-xs rounded-full"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleTagRemove(tag)}
                              className="hover:bg-lex-state-hover rounded-full p-0.5"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Add New Tag */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleTagAdd(newTag)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent text-sm"
                        placeholder="Add tag..."
                      />
                      <button
                        type="button"
                        onClick={() => handleTagAdd(newTag)}
                        className="px-3 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Matter Organization
                        </h4>
                        {formData.organizationRationale && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {formData.organizationRationale}
                          </p>
                        )}
                      </div>
                      {typeof formData.organizationConfidence === 'number' && formData.organizationConfidence > 0 && (
                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {formData.organizationConfidence}%
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Document Type
                        </label>
                        <select
                          value={formData.legalDocumentType || ''}
                          onChange={(e) => {
                            const selectedType = documentTypeOptions.find((type) => type.value === e.target.value);
                            onInputChange('legalDocumentType', e.target.value);
                            if (selectedType) {
                              onInputChange('documentPhase', selectedType.phaseKey);
                              onInputChange('organizationCategoryLabel', selectedType.label);
                            }
                          }}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">Select type...</option>
                          {documentTypeOptions.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Matter Section
                        </label>
                        <select
                          value={formData.documentPhase || ''}
                          onChange={(e) => onInputChange('documentPhase', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">Select section...</option>
                          {matterPhaseOptions.map((phase) => (
                            <option key={phase.value} value={phase.value}>
                              {phase.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Subtype
                        </label>
                        <input
                          type="text"
                          value={formData.legalDocumentSubtype || ''}
                          onChange={(e) => onInputChange('legalDocumentSubtype', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          placeholder="proof_of_service, rfp, minute_order..."
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Suggested Category
                        </label>
                        <input
                          type="text"
                          value={formData.organizationCategoryLabel || ''}
                          onChange={(e) => onInputChange('organizationCategoryLabel', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          placeholder="Proof of Service, Discovery Response..."
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Filing Status
                        </label>
                        <select
                          value={formData.filingStatus || ''}
                          onChange={(e) => onInputChange('filingStatus', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">Select status...</option>
                          <option value="draft">Draft</option>
                          <option value="filed">Filed</option>
                          <option value="served">Served</option>
                          <option value="received">Received</option>
                          <option value="entered">Entered</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Source Role
                        </label>
                        <select
                          value={formData.sourceRole || ''}
                          onChange={(e) => onInputChange('sourceRole', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-lex-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">Select source...</option>
                          <option value="court">Court</option>
                          <option value="client">Client</option>
                          <option value="opposing_counsel">Opposing Counsel</option>
                          <option value="plaintiff">Plaintiff</option>
                          <option value="defendant">Defendant</option>
                          <option value="third_party">Third Party</option>
                          <option value="internal">Internal</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {mode !== 'upload' && (
                    <>
                      {/* Jurisdiction */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4" />
                          Jurisdiction
                        </label>
                        <select
                          value={formData.jurisdiction}
                          onChange={(e) => onInputChange('jurisdiction', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        >
                          <option value="">Select jurisdiction...</option>
                          {mockJurisdictions.map((jurisdiction) => (
                            <option key={jurisdiction.id} value={jurisdiction.id}>
                              {jurisdiction.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Document Purpose */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <FlagIcon className="h-4 w-4" />
                          Document Purpose
                        </label>
                        <select
                          value={formData.documentPurpose}
                          onChange={(e) => onInputChange('documentPurpose', e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        >
                          <option value="">Select purpose...</option>
                          {mockDocumentPurposes.map((purpose) => (
                            <option key={purpose.id} value={purpose.id}>
                              {purpose.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Lifecycle Stage */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <ClockIcon className="h-4 w-4" />
                          Lifecycle Stage
                        </label>
                        <select
                          value={formData.lifecycleStage}
                          onChange={(e) => onInputChange('lifecycleStage', e.target.value as any)}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        >
                          {mockLifecycleStages.map((stage) => (
                            <option key={stage.stage} value={stage.stage}>
                              {stage.icon} {stage.label} - {stage.description}
                            </option>
                          ))}
                        </select>
                        {formData.lifecycleStage && (
                          <div className={`mt-1 px-2 py-1 rounded text-xs font-medium border inline-block ${getLifecycleStageColor(formData.lifecycleStage)}`}>
                            {mockLifecycleStages.find(s => s.stage === formData.lifecycleStage)?.label}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Processing Options Section */}
              {section.id === 'processing-options' && (
                <>
                  {/* AI Processing Toggle */}
                  <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.aiProcessing}
                      onChange={(e) => onInputChange('aiProcessing', e.target.checked)}
                      className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-900 dark:text-white">
                      Enable AI metadata extraction and analysis
                    </label>
                  </div>

                  {/* MCP Processing Profile */}
                  {formData.aiProcessing && (
                    <MCPProcessingProfileSelector
                      selectedProfile={formData.aiProcessingProfile}
                      onProfileChange={(profileId) => onInputChange('aiProcessingProfile', profileId as any)}
                    />
                  )}

                  {/* AI-Only Visibility */}
                  <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.aiOnlyVisibility}
                      onChange={(e) => onInputChange('aiOnlyVisibility', e.target.checked)}
                      className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div>
                      <label className="text-sm font-medium text-gray-900 dark:text-white">
                        AI-Only Visibility
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Make this document available to AI agents only (hidden from user UI unless requested)
                      </p>
                    </div>
                  </div>

                  {/* AI Confidence Preview */}
                  {formData.aiProcessing && uploadedFiles.length > 0 && (
                    <AIConfidencePreview
                      documentFile={uploadedFiles[0]}
                      processingProfile={formData.aiProcessingProfile}
                      onConfidenceUpdate={(confidence) => onInputChange('aiConfidencePreview', confidence.overallConfidence)}
                    />
                  )}
                </>
              )}

              {/* Dates & Reminders Section */}
              {section.id === 'dates-reminders' && (
                <>
                  {/* Effective Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Effective Date
                    </label>
                    <input
                      type="date"
                      value={formData.effectiveDate}
                      onChange={(e) => onInputChange('effectiveDate', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                    />
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => onInputChange('expiryDate', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                    />
                  </div>

                  {/* Reminder Settings */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.reminderEnabled}
                        onChange={(e) => onInputChange('reminderEnabled', e.target.checked)}
                        className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                      />
                      <label className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <BellIcon className="h-4 w-4" />
                        Enable Reminder
                      </label>
                    </div>

                    {formData.reminderEnabled && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          Remind me before expiry
                        </label>
                        <select
                          value={formData.reminderDays}
                          onChange={(e) => onInputChange('reminderDays', parseInt(e.target.value))}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        >
                          <option value={1}>1 day before</option>
                          <option value={3}>3 days before</option>
                          <option value={7}>1 week before</option>
                          <option value={14}>2 weeks before</option>
                          <option value={30}>1 month before</option>
                        </select>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Security & Compliance Section */}
              {section.id === 'security-compliance' && (
                <>
                  {/* Confidentiality */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <ShieldCheckIcon className="h-4 w-4" />
                      Confidentiality
                    </label>
                    <select
                      value={formData.confidentiality}
                      onChange={(e) => onInputChange('confidentiality', e.target.value as any)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                    >
                      <option value="public">Public</option>
                      <option value="internal">Internal</option>
                      <option value="confidential">Confidential</option>
                      <option value="restricted">Restricted</option>
                    </select>
                    <div className={`mt-1 px-2 py-1 rounded text-xs font-medium border inline-block ${getConfidentialityColor(formData.confidentiality)}`}>
                      {formData.confidentiality.charAt(0).toUpperCase() + formData.confidentiality.slice(1)}
                    </div>
                  </div>

                  {/* Retention Policy */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <ArchiveBoxIcon className="h-4 w-4" />
                      Retention Policy
                    </label>
                    <select
                      value={formData.retentionPolicy}
                      onChange={(e) => onInputChange('retentionPolicy', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                    >
                      <option value="">Select retention policy...</option>
                      {mockRetentionPolicies.map((policy) => (
                        <option key={policy.id} value={policy.id}>
                          {policy.name}
                        </option>
                      ))}
                    </select>
                    {formData.retentionPolicy && (
                      <div className="mt-2 p-2 bg-lex-accent-soft border border-lex-accent rounded-lg">
                        <p className="text-xs text-lex-accent">
                          <strong>Auto-archive date:</strong> {calculateAutoArchiveDate(formData.retentionPolicy) || 'Based on case closure'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Responsible Attorney */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      Responsible Attorney
                    </label>
                    <input
                      type="text"
                      value={formData.responsibleAttorney}
                      onChange={(e) => onInputChange('responsibleAttorney', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                      placeholder="Attorney name..."
                    />
                  </div>
                </>
              )}

              {/* Relations & Versioning Section */}
              {section.id === 'relations-versioning' && (
                <>
                  {/* AI Related Documents */}
                  <AIRelatedDocuments
                    currentDocument={{
                      title: formData.title,
                      description: formData.description,
                      clientId: formData.clientId,
                      matterId: formData.matterId,
                      tags: formData.tags
                    }}
                    selectedDocuments={formData.relatedDocuments}
                    onDocumentsSuggested={(documents) => onInputChange('aiSuggestedRelations', documents)}
                    onDocumentAdd={(documentId) => {
                      const updated = [...formData.relatedDocuments, documentId];
                      onInputChange('relatedDocuments', updated);
                    }}
                  />

                  {/* Version Management */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <ClockIcon className="h-4 w-4" />
                        Version
                      </label>
                      <input
                        type="text"
                        value={formData.version}
                        onChange={(e) => onInputChange('version', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        placeholder="1.0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Custom Label
                      </label>
                      <input
                        type="text"
                        value={formData.customVersionLabel}
                        onChange={(e) => onInputChange('customVersionLabel', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        placeholder="e.g., Final, Draft, Review"
                      />
                    </div>
                  </div>

                  {/* Version Notes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Version Notes
                    </label>
                    <textarea
                      value={formData.versionNotes}
                      onChange={(e) => onInputChange('versionNotes', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                      placeholder="Describe changes in this version..."
                      rows={2}
                    />
                  </div>
                </>
              )}


            </div>
          )}
        </div>
      ))}
      {/* Portal-based client dropdown */}
      {clientSearch.length > 0 && !clientSelected && dropdownPosition && 
        createPortal(
          <div 
            className="fixed z-[10060] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl max-h-48 overflow-y-auto"
            style={{
              top: dropdownPosition?.top || 0,
              left: dropdownPosition?.left || 0,
              width: dropdownPosition?.width || 384,
              zIndex: 10060
            }}
          >
            {clientSearchLoading ? (
              <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent"></div>
                Searching clients...
              </div>
            ) : clientSearchError ? (
              <div className="px-3 py-2 text-red-500 dark:text-red-400 text-sm">
                Error: {clientSearchError}
              </div>
            ) : contacts.length > 0 ? (
              contacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => {
                    onInputChange('clientId', contact.id);
                    setClientSearchWithLogging(contact.name || '');
                    setClientSearchLoading(false);
                    setClientSearchError(null);
                    setClientSelected(true); // Mark as selected to hide dropdown
                  }}
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {contact.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {contact.email}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                No clients found matching "{clientSearch}"
              </div>
            )}
          </div>,
          document.body
        )
      }
    </div>
  );
};
